/**
 * Business Valuation Dashboard
 * 
 * Comprehensive financial dashboard with business valuation metrics,
 * P&L summaries, and industry benchmarks. Calendar year based.
 * Accessible by: Owner, Manager, Accountant (read-only)
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, DollarSign, BarChart3, PieChart,
  Calendar, RefreshCw, Download, AlertTriangle, Check, 
  Building2, Calculator, FileText, ChevronLeft, ChevronRight,
  Lock, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Format currency with $ for admin
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatCurrencyFull = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

// Health indicator component
const HealthIndicator = ({ healthy, label }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
    healthy ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
  }`}>
    {healthy ? (
      <Check className="w-4 h-4 text-green-600" />
    ) : (
      <AlertTriangle className="w-4 h-4 text-red-600" />
    )}
    <span className={`text-sm font-medium ${healthy ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
      {label}
    </span>
  </div>
);

// Valuation range component
const ValuationRange = ({ low, mid, high, label }) => (
  <div className="space-y-2">
    <p className="text-sm text-muted-foreground">{label}</p>
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">{formatCurrency(low)}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-amber-500 via-green-500 to-emerald-500"
          style={{ width: '100%' }}
        />
      </div>
      <span className="text-sm text-muted-foreground">{formatCurrency(high)}</span>
    </div>
    <p className="text-center text-lg font-bold text-foreground">{formatCurrency(mid)}</p>
  </div>
);

export default function BusinessValuationTab() {
  const [dashboardData, setDashboardData] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedPeriod, setSelectedPeriod] = useState('ytd');
  
  // Fetch permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/valuation/accountant-access');
      setPermissions(res.data);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
  }, []);
  
  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/finance/valuation/dashboard?year=${selectedYear}`);
      setDashboardData(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      toast.error('Failed to load valuation data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);
  
  // Fetch financial summary
  const fetchFinancialSummary = useCallback(async () => {
    try {
      const res = await api.get(`/admin/finance/valuation/financial-summary?period=${selectedPeriod}`);
      setFinancialSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch financial summary:', err);
    }
  }, [selectedPeriod]);
  
  // Export P&L
  const handleExportPnL = async () => {
    try {
      const res = await api.get(`/admin/finance/valuation/export/pnl?year=${selectedYear}&format=html`);
      // Open HTML in new window for printing - HTML is generated server-side and sanitized
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups for this site');
        return;
      }
      printWindow.document.open();
      printWindow.document.write(res.data.html);
      printWindow.document.close();
      toast.success('P&L report opened - Print or save as PDF');
    } catch (err) {
      toast.error('Failed to export report');
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchPermissions();
    fetchDashboard();
    fetchFinancialSummary();
  }, [fetchPermissions, fetchDashboard, fetchFinancialSummary]);
  
  // Refresh when year changes
  useEffect(() => {
    fetchDashboard();
  }, [selectedYear, fetchDashboard]);
  
  // Refresh when period changes
  useEffect(() => {
    fetchFinancialSummary();
  }, [selectedPeriod, fetchFinancialSummary]);
  
  // Year navigation
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  
  return (
    <div className="space-y-6" data-testid="business-valuation-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-heading text-foreground">Business Valuation</h2>
            {permissions?.read_only && (
              <Badge variant="secondary" className="gap-1">
                <Eye className="w-3 h-3" />
                Read Only
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            {permissions?.role_display || 'Loading...'} - Calendar Year {selectedYear}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSelectedYear(y => Math.max(y - 1, currentYear - 10))}
              disabled={selectedYear <= currentYear - 10}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-3 font-medium">{selectedYear}</span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSelectedYear(y => Math.min(y + 1, currentYear))}
              disabled={selectedYear >= currentYear}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchFinancialSummary(); }}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={handleExportPnL}>
            <Download className="w-4 h-4 mr-2" />
            Export P&L
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : dashboardData ? (
        <>
          {/* Estimated Business Value */}
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Business Value</p>
                  <p className="text-4xl font-bold text-foreground">
                    {formatCurrency(dashboardData.estimated_value?.mid)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Range: {formatCurrency(dashboardData.estimated_value?.low)} - {formatCurrency(dashboardData.estimated_value?.high)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-12 h-12 text-primary/50" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Annual Revenue</p>
                    <p className="text-xl font-bold text-foreground">
                      {formatCurrency(dashboardData.annual_summary?.revenue)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    dashboardData.health_indicators?.profitable 
                      ? 'bg-green-100 dark:bg-green-900/30' 
                      : 'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    {dashboardData.annual_summary?.net_profit >= 0 ? (
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Net Profit</p>
                    <p className={`text-xl font-bold ${
                      dashboardData.annual_summary?.net_profit >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(dashboardData.annual_summary?.net_profit)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <PieChart className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Net Margin</p>
                    <p className="text-xl font-bold text-foreground">
                      {formatPercent(dashboardData.margins?.net_margin_percent)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Calculator className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">SDE</p>
                    <p className="text-xl font-bold text-foreground">
                      {formatCurrency(dashboardData.annual_summary?.sde)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Health Indicators */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Financial Health Check</CardTitle>
              <CardDescription>Based on café industry benchmarks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <HealthIndicator 
                  healthy={dashboardData.health_indicators?.profitable} 
                  label="Profitable" 
                />
                <HealthIndicator 
                  healthy={dashboardData.health_indicators?.gross_margin_healthy} 
                  label={`Gross Margin ${formatPercent(dashboardData.margins?.gross_margin_percent)}`}
                />
                <HealthIndicator 
                  healthy={dashboardData.health_indicators?.food_cost_healthy} 
                  label={`Food Cost ${formatPercent(dashboardData.margins?.cogs_percent)}`}
                />
                <HealthIndicator 
                  healthy={dashboardData.health_indicators?.labor_cost_healthy} 
                  label={`Labor Cost ${formatPercent(dashboardData.margins?.payroll_percent)}`}
                />
                <HealthIndicator 
                  healthy={dashboardData.health_indicators?.prime_cost_healthy} 
                  label={`Prime Cost ${formatPercent(dashboardData.margins?.prime_cost_percent)}`}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Valuation Methods */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Revenue Multiple</CardTitle>
                <CardDescription className="text-xs">{dashboardData.valuations?.revenue_based?.method}</CardDescription>
              </CardHeader>
              <CardContent>
                <ValuationRange
                  low={dashboardData.valuations?.revenue_based?.low}
                  mid={dashboardData.valuations?.revenue_based?.mid}
                  high={dashboardData.valuations?.revenue_based?.high}
                  label="0.3x - 0.75x Annual Revenue"
                />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">SDE Multiple</CardTitle>
                <CardDescription className="text-xs">{dashboardData.valuations?.sde_based?.method}</CardDescription>
              </CardHeader>
              <CardContent>
                <ValuationRange
                  low={dashboardData.valuations?.sde_based?.low}
                  mid={dashboardData.valuations?.sde_based?.mid}
                  high={dashboardData.valuations?.sde_based?.high}
                  label="1.5x - 3.5x SDE"
                />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  SDE: {formatCurrency(dashboardData.valuations?.sde_based?.sde)}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">EBITDA Multiple</CardTitle>
                <CardDescription className="text-xs">{dashboardData.valuations?.ebitda_based?.method}</CardDescription>
              </CardHeader>
              <CardContent>
                <ValuationRange
                  low={dashboardData.valuations?.ebitda_based?.low}
                  mid={dashboardData.valuations?.ebitda_based?.mid}
                  high={dashboardData.valuations?.ebitda_based?.high}
                  label="2x - 4x EBITDA"
                />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  EBITDA: {formatCurrency(dashboardData.valuations?.ebitda_based?.ebitda)}
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* P&L Summary */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Profit & Loss Summary
                  </CardTitle>
                  <CardDescription>Calendar Year {selectedYear}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Revenue */}
                <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <span className="font-medium">Total Revenue</span>
                  <span className="font-bold text-blue-600">{formatCurrencyFull(dashboardData.annual_summary?.revenue)}</span>
                </div>
                
                {/* COGS */}
                <div className="flex justify-between items-center pl-6">
                  <span className="text-muted-foreground">Less: Cost of Goods Sold ({formatPercent(dashboardData.margins?.cogs_percent)})</span>
                  <span className="text-red-600">-{formatCurrencyFull(dashboardData.annual_summary?.cogs)}</span>
                </div>
                
                {/* Gross Profit */}
                <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span className="font-medium">Gross Profit ({formatPercent(dashboardData.margins?.gross_margin_percent)} margin)</span>
                  <span className="font-bold text-green-600">{formatCurrencyFull(dashboardData.annual_summary?.gross_profit)}</span>
                </div>
                
                {/* Payroll */}
                <div className="flex justify-between items-center pl-6">
                  <span className="text-muted-foreground">Less: Payroll ({formatPercent(dashboardData.margins?.payroll_percent)})</span>
                  <span className="text-red-600">-{formatCurrencyFull(dashboardData.annual_summary?.payroll)}</span>
                </div>
                
                {/* Overhead */}
                <div className="flex justify-between items-center pl-6">
                  <span className="text-muted-foreground">Less: Overhead ({formatPercent(dashboardData.margins?.overhead_percent)})</span>
                  <span className="text-red-600">-{formatCurrencyFull(dashboardData.annual_summary?.overhead)}</span>
                </div>
                
                {/* Net Profit */}
                <div className={`flex justify-between items-center p-3 rounded-lg ${
                  dashboardData.annual_summary?.net_profit >= 0 
                    ? 'bg-green-100 dark:bg-green-900/30' 
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  <span className="font-bold">Net Profit ({formatPercent(dashboardData.margins?.net_margin_percent)} margin)</span>
                  <span className={`text-xl font-bold ${
                    dashboardData.annual_summary?.net_profit >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrencyFull(dashboardData.annual_summary?.net_profit)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Monthly Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Month</th>
                      <th className="text-right p-2">Revenue</th>
                      <th className="text-right p-2">Expenses</th>
                      <th className="text-right p-2">Net Profit</th>
                      <th className="text-right p-2">Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.monthly_breakdown?.map((month, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/30">
                        <td className="p-2 font-medium">{month.month_name}</td>
                        <td className="p-2 text-right">{formatCurrency(month.revenue)}</td>
                        <td className="p-2 text-right text-red-600">-{formatCurrency(month.expenses)}</td>
                        <td className={`p-2 text-right font-medium ${month.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(month.net_profit)}
                        </td>
                        <td className="p-2 text-right text-muted-foreground">{month.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          {/* YoY Comparison */}
          {dashboardData.yoy_comparison && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Year-over-Year Comparison</CardTitle>
                <CardDescription>{selectedYear} vs {selectedYear - 1}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Revenue Change</p>
                    <p className={`text-2xl font-bold ${dashboardData.yoy_comparison.revenue_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dashboardData.yoy_comparison.revenue_change_percent >= 0 ? '+' : ''}
                      {formatPercent(dashboardData.yoy_comparison.revenue_change_percent)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(dashboardData.yoy_comparison.revenue_change)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Expense Change</p>
                    <p className={`text-2xl font-bold ${dashboardData.yoy_comparison.expense_change_percent <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dashboardData.yoy_comparison.expense_change_percent >= 0 ? '+' : ''}
                      {formatPercent(dashboardData.yoy_comparison.expense_change_percent)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(dashboardData.yoy_comparison.expense_change)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">Previous Net Profit</p>
                    <p className={`text-2xl font-bold ${dashboardData.yoy_comparison.prev_net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(dashboardData.yoy_comparison.prev_net_profit)}
                    </p>
                    <p className="text-xs text-muted-foreground">{selectedYear - 1}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Accountant Note */}
          {permissions?.read_only && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-400">Accountant Access</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      You have read-only access to financial data. Contact the owner or manager to make changes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No data available for {selectedYear}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
